import { createFileRoute, Link } from "@tanstack/react-router";
import { DealLink, DealDate } from "@/components/deal-link";
import { useMemo, useState } from "react";
import { Header, Footer } from "@/components/layout";
import { Breadcrumbs } from "@/components/breadcrumbs";
import { buildMeta } from "@/lib/seo";
import { getGlobalDeals, type GlobalDeal } from "@/lib/global-deals.functions";
import { setEdgeCache } from "@/lib/ssr-cache";
import { breadcrumbScript } from "@/lib/breadcrumbs";
import { Flame, TrendingDown, Plane, X, ArrowUpDown } from "lucide-react";
import { PriceBadge } from "@/components/price-badge";
import { REGIONS, regionOf, type Region } from "@/lib/regions";

type SortMode = "price" | "country" | "discount";

export const Route = createFileRoute("/okazje")({
  loader: async () => {
    // Strona stricte SEO + viral — content w 100% z cache, edge cache 30 min.
    await setEdgeCache({ sMaxAge: 1800, swr: 86400 });
    const deals = await getGlobalDeals({ data: { limit: 60 } }).catch(() => [] as GlobalDeal[]);
    return { deals };
  },
  head: ({ loaderData }) => {
    const count = loaderData?.deals.length ?? 0;
    const minPrice = loaderData?.deals[0]?.priceNumeric;
    const titleSuffix = minPrice ? ` — od ${minPrice} zł` : "";
    const base = buildMeta({
      title: `Tanie loty okazje${titleSuffix} | Tanie Latanie`,
      description: `Top ${count || 50} najtańszych lotów z Polski na ten tydzień. Aktualne ceny, bez ukrytych opłat. Zarezerwuj zanim cena wzrośnie.`,
      path: "/okazje",
    });
    return {
      ...base,
      scripts: [
        ...(base.scripts ?? []),
        breadcrumbScript([
          { name: "Strona główna", path: "/" },
          { name: "Okazje", path: "/okazje" },
        ]),
      ],
    };
  },
  component: OkazjePage,
});

function OkazjePage() {
  const { deals } = Route.useLoaderData();

  // Pełna lista krajów + lotnisk wylotu + dostępne regiony + max ceny. Liczone raz, na podstawie cache.
  const { countries, origins, availableRegions, priceCeiling } = useMemo(() => {
    const countrySet = new Set<string>();
    const originMap = new Map<string, string>(); // IATA -> city
    const regionSet = new Set<Region>();
    let max = 0;
    for (const d of deals) {
      countrySet.add(d.toCountry);
      if (!originMap.has(d.from)) originMap.set(d.from, d.fromCity);
      const r = regionOf(d.toCountry);
      if (r) regionSet.add(r);
      if (d.priceNumeric > max) max = d.priceNumeric;
    }
    const ceiling = max > 0 ? Math.ceil(max / 50) * 50 : 2000;
    return {
      countries: [...countrySet].sort((a, b) => a.localeCompare(b, "pl")),
      origins: [...originMap.entries()]
        .map(([iata, city]) => ({ iata, city }))
        .sort((a, b) => a.city.localeCompare(b.city, "pl") || a.iata.localeCompare(b.iata)),
      availableRegions: REGIONS.filter((r) => regionSet.has(r)),
      priceCeiling: ceiling,
    };
  }, [deals]);

  const [selectedCountries, setSelectedCountries] = useState<Set<string>>(new Set());
  const [selectedOrigins, setSelectedOrigins] = useState<Set<string>>(new Set());
  const [selectedRegions, setSelectedRegions] = useState<Set<Region>>(new Set());
  const [maxPrice, setMaxPrice] = useState<number>(priceCeiling);
  const [sort, setSort] = useState<SortMode>("price");

  const filtered = useMemo(() => {
    let out = deals.filter((d: GlobalDeal) => {
      if (d.priceNumeric > maxPrice) return false;
      if (selectedCountries.size > 0 && !selectedCountries.has(d.toCountry)) return false;
      if (selectedOrigins.size > 0 && !selectedOrigins.has(d.from)) return false;
      if (selectedRegions.size > 0) {
        const r = regionOf(d.toCountry);
        if (!r || !selectedRegions.has(r)) return false;
      }
      return true;
    });
    if (sort === "country") {
      out = [...out].sort((a, b) => {
        const c = a.toCountry.localeCompare(b.toCountry, "pl");
        return c !== 0 ? c : a.priceNumeric - b.priceNumeric;
      });
    } else if (sort === "discount") {
      // Najpierw oferty z największym spadkiem vs średnia 30d, potem reszta po cenie.
      out = [...out].sort((a, b) => {
        const da = a.badge?.deltaPct ?? 999;
        const db = b.badge?.deltaPct ?? 999;
        if (da !== db) return da - db;
        return a.priceNumeric - b.priceNumeric;
      });
    }
    // price sort = już posortowane z backendu (ASC po display_price)
    return out;
  }, [deals, maxPrice, selectedCountries, selectedOrigins, selectedRegions, sort]);

  const toggleCountry = (c: string) => {
    setSelectedCountries((prev) => {
      const next = new Set(prev);
      if (next.has(c)) next.delete(c);
      else next.add(c);
      return next;
    });
  };

  const toggleOrigin = (iata: string) => {
    setSelectedOrigins((prev) => {
      const next = new Set(prev);
      if (next.has(iata)) next.delete(iata);
      else next.add(iata);
      return next;
    });
  };

  const toggleRegion = (r: Region) => {
    setSelectedRegions((prev) => {
      const next = new Set(prev);
      if (next.has(r)) next.delete(r);
      else next.add(r);
      return next;
    });
  };

  const resetFilters = () => {
    setSelectedCountries(new Set());
    setSelectedOrigins(new Set());
    setSelectedRegions(new Set());
    setMaxPrice(priceCeiling);
    setSort("price");
  };

  const filtersActive =
    selectedCountries.size > 0 ||
    selectedOrigins.size > 0 ||
    selectedRegions.size > 0 ||
    maxPrice < priceCeiling ||
    sort !== "price";

  return (
    <div className="min-h-screen">
      <Header />

      <section className="container mx-auto px-4 pt-6 pb-2">
        <Breadcrumbs
          items={[
            { name: "Strona główna", path: "/" },
            { name: "Okazje", path: "/okazje" },
          ]}
        />
      </section>

      <section className="container mx-auto px-4 pb-8 pt-6">
        <div className="mx-auto max-w-3xl text-center">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full glass px-4 py-1.5 text-xs font-medium uppercase tracking-widest">
            <Flame className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
            Aktualne najtańsze loty
          </div>
          <h1 className="font-display text-4xl font-bold leading-tight md:text-5xl">
            Tanie loty — <span className="text-gradient-sunset">okazje tygodnia</span>
          </h1>
          <p className="mt-4 text-lg text-muted-foreground">
            Top {deals.length} najtańszych biletów lotniczych z Polski. Ceny odświeżane co kilka
            godzin — kliknij, by zobaczyć aktualne terminy.
          </p>
        </div>
      </section>

      {/* Filtry — czysto client-side, dane z edge cache. Filtruje już załadowaną listę. */}
      {deals.length > 0 && (
        <section className="container mx-auto px-4 pb-6">
          <div className="glass rounded-xl p-4 md:p-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                Filtry
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <ArrowUpDown className="h-3.5 w-3.5" aria-hidden="true" />
                  <button
                    type="button"
                    onClick={() => setSort("price")}
                    className={`rounded-full px-3 py-1 text-xs font-medium transition ${sort === "price" ? "bg-primary text-primary-foreground" : "bg-muted/30 text-foreground hover:bg-muted/50"}`}
                  >
                    Cena ↑
                  </button>
                  <button
                    type="button"
                    onClick={() => setSort("discount")}
                    className={`rounded-full px-3 py-1 text-xs font-medium transition ${sort === "discount" ? "bg-primary text-primary-foreground" : "bg-muted/30 text-foreground hover:bg-muted/50"}`}
                    title="Najpierw oferty z największym spadkiem vs średnia 30 dni"
                  >
                    Spadek ceny
                  </button>
                  <button
                    type="button"
                    onClick={() => setSort("country")}
                    className={`rounded-full px-3 py-1 text-xs font-medium transition ${sort === "country" ? "bg-primary text-primary-foreground" : "bg-muted/30 text-foreground hover:bg-muted/50"}`}
                  >
                    Kraj A-Z
                  </button>
                </div>
                {filtersActive && (
                  <button
                    type="button"
                    onClick={resetFilters}
                    className="inline-flex items-center gap-1 rounded-full bg-muted/30 px-3 py-1 text-xs font-medium hover:bg-muted/50"
                  >
                    <X className="h-3 w-3" aria-hidden="true" />
                    Wyczyść
                  </button>
                )}
              </div>
            </div>

            {/* Slider max ceny */}
            <div className="mt-4">
              <label htmlFor="maxPrice" className="mb-2 flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Maksymalna cena</span>
                <span className="font-semibold text-gradient-sunset">do {maxPrice} zł</span>
              </label>
              <input
                id="maxPrice"
                type="range"
                min={100}
                max={priceCeiling}
                step={50}
                value={maxPrice}
                onChange={(e) => setMaxPrice(Number(e.target.value))}
                style={{
                  background: `linear-gradient(to right, var(--primary) 0%, var(--primary) ${((maxPrice - 100) / Math.max(1, priceCeiling - 100)) * 100}%, var(--muted) ${((maxPrice - 100) / Math.max(1, priceCeiling - 100)) * 100}%, var(--muted) 100%)`,
                }}
                className="price-range h-2 w-full cursor-pointer appearance-none rounded-full accent-primary"
              />
            </div>

            {/* Region docelowy chips */}
            {availableRegions.length > 1 && (
              <div className="mt-4">
                <div className="mb-2 text-xs text-muted-foreground">
                  Region{" "}
                  {selectedRegions.size > 0 && (
                    <span className="text-primary">({selectedRegions.size} wybranych)</span>
                  )}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {availableRegions.map((r) => {
                    const active = selectedRegions.has(r);
                    return (
                      <button
                        key={r}
                        type="button"
                        onClick={() => toggleRegion(r)}
                        className={`rounded-full px-2.5 py-1 text-xs font-medium transition ${active ? "bg-primary text-primary-foreground shadow-glow" : "bg-muted/30 text-foreground hover:bg-muted/50"}`}
                      >
                        {r}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Lotnisko wylotu chips */}
            {origins.length > 1 && (
              <div className="mt-4">
                <div className="mb-2 text-xs text-muted-foreground">
                  Lotnisko wylotu{" "}
                  {selectedOrigins.size > 0 && (
                    <span className="text-primary">({selectedOrigins.size} wybranych)</span>
                  )}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {origins.map((o) => {
                    const active = selectedOrigins.has(o.iata);
                    return (
                      <button
                        key={o.iata}
                        type="button"
                        onClick={() => toggleOrigin(o.iata)}
                        className={`rounded-full px-2.5 py-1 text-xs font-medium transition ${active ? "bg-primary text-primary-foreground shadow-glow" : "bg-muted/30 text-foreground hover:bg-muted/50"}`}
                        title={`${o.city} (${o.iata})`}
                      >
                        <span className="font-mono">{o.iata}</span>
                        <span className="ml-1 opacity-70">· {o.city}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Country chips */}
            <div className="mt-4">
              <div className="mb-2 text-xs text-muted-foreground">
                Kraj docelowy{" "}
                {selectedCountries.size > 0 && (
                  <span className="text-primary">({selectedCountries.size} wybranych)</span>
                )}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {countries.map((c) => {
                  const active = selectedCountries.has(c);
                  return (
                    <button
                      key={c}
                      type="button"
                      onClick={() => toggleCountry(c)}
                      className={`rounded-full px-2.5 py-1 text-xs font-medium transition ${active ? "bg-primary text-primary-foreground shadow-glow" : "bg-muted/30 text-foreground hover:bg-muted/50"}`}
                    >
                      {c}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </section>
      )}

      <section className="container mx-auto px-4 pb-16">
        {deals.length === 0 ? (
          <div className="glass mx-auto max-w-xl rounded-lg p-8 text-center text-muted-foreground">
            Chwilowo nie mamy świeżych cen. Wróć za chwilę lub skorzystaj z{" "}
            <Link to="/" className="text-primary hover:underline">
              wyszukiwarki
            </Link>
            .
          </div>
        ) : filtered.length === 0 ? (
          <div className="glass mx-auto max-w-xl rounded-lg p-8 text-center text-muted-foreground">
            Brak okazji pasujących do wybranych filtrów.{" "}
            <button onClick={resetFilters} className="text-primary hover:underline">
              Wyczyść filtry
            </button>
          </div>
        ) : (
          <>
            <div className="mb-4 text-sm text-muted-foreground">
              Znaleziono <span className="font-semibold text-foreground">{filtered.length}</span>{" "}
              okazji
            </div>
            <ol className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((d: GlobalDeal, i: number) => (
                <li key={d.from + d.to}>
                  <DealLink
                    from={d.from}
                    to={d.to}
                    departDate={d.departDate}
                    className="glass group flex items-center gap-2.5 rounded-lg p-3 transition-all hover:shadow-glow sm:gap-3 sm:p-4"
                    title={`Tanie loty ${d.fromCity} → ${d.toCity}`}
                  >
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 font-mono text-xs font-bold text-primary sm:h-10 sm:w-10 sm:text-sm">
                      {i + 1}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 font-mono text-[11px] text-muted-foreground">
                        <Plane className="h-3 w-3" aria-hidden="true" />
                        {d.from} → {d.to}
                      </div>
                      <div className="truncate font-display text-sm font-bold group-hover:text-primary sm:text-base">
                        {d.fromCity} → {d.toCity}
                      </div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                        <span className="truncate text-xs text-muted-foreground">
                          {d.toCountry}
                        </span>
                        {d.badge && <PriceBadge badge={d.badge} size="xs" />}
                      </div>
                    </div>
                    <div className="shrink-0 text-right">
                      <div className="hidden text-[10px] uppercase text-muted-foreground sm:block">
                        cena
                      </div>
                      <div className="whitespace-nowrap text-sm font-semibold text-gradient-sunset sm:text-base">
                        {d.price}
                      </div>
                      <DealDate departDate={d.departDate} />
                    </div>
                  </DealLink>
                </li>
              ))}
            </ol>
          </>
        )}
      </section>

      <section className="container mx-auto max-w-3xl px-4 pb-16">
        <div className="glass rounded-lg p-6">
          <div className="mb-3 flex items-center gap-2">
            <TrendingDown className="h-5 w-5 text-primary" aria-hidden="true" />
            <h2 className="font-display text-xl font-bold">Skąd te ceny?</h2>
          </div>
          <p className="text-sm text-muted-foreground">
            Codziennie przeszukujemy oferty wszystkich linii lotniczych świata dla 100+ tras
            najczęściej wyszukiwanych przez Polaków. Cena „od" odzwierciedla najtańszą znalezioną
            ofertę w najbliższych 30–90 dniach. Po kliknięciu zobaczysz aktualne terminy i pełną
            ofertę z możliwością rezerwacji.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
